import * as Sentry from '@sentry/nextjs';

interface CaptureErrorOptions {
  error: Error;
  context: string;
  action: string;
  level?: 'error' | 'warning' | 'info';
  requestData?: Record<string, any>;
  responseData?: Record<string, any>;
  metadata?: Record<string, any>;
}

export function captureWithContext({
  error,
  context,
  action,
  level = 'error',
  requestData,
  responseData,
  metadata,
}: CaptureErrorOptions): void {
  Sentry.withScope((scope) => {
    scope.setTag('context', context);
    scope.setTag(`${context}_action`, action);

    if (requestData) scope.setContext('request', requestData);
    if (responseData) scope.setContext('response', responseData);
    if (metadata) scope.setContext('metadata', metadata);

    scope.setLevel(level);

    Sentry.captureException(error);
  });
}

/**
 * Drop-in replacement for console.error that also reports to Sentry with
 * structured context. Use this in catch blocks that previously only did
 * console.error so that errors become visible in Sentry dashboards.
 *
 * Note: The captureConsoleIntegration already captures all console.error
 * calls as a safety net. Use reportError when you want richer tagging
 * (source, extra context) for easier triage.
 */
export function reportError(
  message: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  console.error(message, error);
  Sentry.withScope((scope) => {
    if (context?.source) scope.setTag('source', String(context.source));
    scope.setContext('reportError', { message, ...context });
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}
